import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import "./MatchModal.css";
import { ayreneService } from "../../services/ayrene.service";
import { useNavigate } from "react-router-dom";

interface MatchModalProps {
  userName: string;
  matchedUserId?: string;
  matchedUserEmail: string;
  onClose: () => void;
  onViewProfile: () => void;
}

type AyreneUserStatus = 'idle' | 'loading' | 'success' | 'notFound' | 'error';

const MatchModal: React.FC<MatchModalProps> = ({
  userName,
  matchedUserEmail,
  onClose,
}) => {
  const [ayreneStatus, setAyreneStatus] = useState<AyreneUserStatus>('idle');
  const [ayreneUserId, setAyreneUserId] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    const lookupAyreneUser = async () => {
      if (!matchedUserEmail) {
        setAyreneStatus('error');
        setErrorMessage('Email not available');
        return;
      }

      setAyreneStatus('loading');

      try {
        const response = await ayreneService.getUserByEmail(matchedUserEmail);

        if (response.success && response.ayreneUserId) {
          setAyreneStatus('success');
          setAyreneUserId(response.ayreneUserId);
        } else if (response.message === 'User not found on Ayrene') {
          setAyreneStatus('notFound');
        } else {
          setAyreneStatus('error');
          setErrorMessage(response.message || 'Failed to look up user');
        }
      } catch (error) {
        console.error('Error looking up Ayrene user:', error);
        setAyreneStatus('error');
        setErrorMessage('Network error. Please try again.');
      }
    };

    lookupAyreneUser();
  }, [matchedUserEmail]);

  const handleStartChat = () => {
    if (ayreneUserId) {
      window.open(`/chat?userId=${ayreneUserId}`, '_blank');
    }
  };

  const handleRetry = () => {
    setAyreneStatus('idle');
    const lookupAyreneUser = async () => {
      if (!matchedUserEmail) {
        setAyreneStatus('error');
        setErrorMessage('Email not available');
        return;
      }

      setAyreneStatus('loading');

      try {
        const response = await ayreneService.getUserByEmail(matchedUserEmail);

        if (response.success && response.ayreneUserId) {
          setAyreneStatus('success');
          setAyreneUserId(response.ayreneUserId);
        } else if (response.message === 'User not found on Ayrene') {
          setAyreneStatus('notFound');
        } else {
          setAyreneStatus('error');
          setErrorMessage(response.message || 'Failed to look up user');
        }
      } catch (error) {
        console.error('Error looking up Ayrene user:', error);
        setAyreneStatus('error');
        setErrorMessage('Network error. Please try again.');
      }
    };

    lookupAyreneUser();
  };
  const modalContent = (
    <div className="match-modal-backdrop">
      <div className="match-modal">
        <h3 className="match-msg">
          🎉 You and <span>{userName}</span> have matched!
        </h3>

        {ayreneStatus === 'loading' && (
          <p className="ayrene-status-message">Loading chat info...</p>
        )}

        {ayreneStatus === 'notFound' && (
          <p className="ayrene-status-message ayrene-not-found">
            This user hasn't joined the chat yet. Encourage them to sign up!
          </p>
        )}

        {ayreneStatus === 'error' && (
          <div className="ayrene-error-container">
            <p className="ayrene-status-message ayrene-error">
              Unable to connect. Please try again later.
            </p>
            <button className="retry-btn" onClick={handleRetry}>
              🔄 Retry
            </button>
          </div>
        )}

        <div className="match-buttons">
          {ayreneStatus === 'success' && (
            <button
              className="start-chat-btn"
              onClick={handleStartChat}
            >
              💬 Start Chat
            </button>
          )}
          <button className="close-btn" onClick={onClose}>
            ❌ Close
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default MatchModal;
